/**
 * Générateur — Lire l'abscisse d'un nombre relatif sur une droite graduée et
 * placer un nombre relatif d'abscisse donnée.
 * Attendu ID : 914 | Niveau : 5ème | Domaine : Nombres relatifs
 *
 * Libellé officiel (spécification 914.json) : « Lire l'abscisse d'un nombre
 * relatif sur une droite graduée et placer un nombre relatif d'abscisse
 * donnée. »
 *
 * Plan de cartes (version conforme à la spécification, campagne 5e 2026-09-22) :
 *
 *   Carte « lire » — Lire l'abscisse d'un point (niveaux 1 à 5)
 *     Élément numberline natif ; la fenêtre ne commence JAMAIS à zéro, ses
 *     bornes ne sont pas étiquetées, et seules deux ou trois graduations
 *     principales le sont — l'élève doit compter à partir d'elles.
 *     texte_trou_drag, qcm_unique (distracteurs = les erreurs de comptage),
 *     texte (saisie décimale), tableau_vf (cinq gabarits à prédicat variable),
 *     et texte sur une droite graduée en TIERS, où la réponse attendue est une
 *     fraction et jamais une valeur décimale approchée.
 *
 *   Carte « placer » — Placer des points sur la droite graduée (niveaux 1 à 5)
 *     Format interactif placer_points (snap sur la graduation ; la tolérance
 *     du composant est exactement la moitié du pas de la question).
 *     Un format par graduation secondaire de la liste fermée de la spec :
 *       placer_unite      (pas 1)     — niveau 1
 *       placer_demis      (pas 0,5)   — niveau 2
 *       placer_quarts     (pas 0,25)  — niveau 2
 *       placer_dixiemes   (pas 0,1)   — niveau 3
 *       placer_centiemes  (pas 0,01)  — niveau 5, avec au moins une abscisse
 *                                       écrite à une seule décimale (erreur
 *                                       visée « placer -1,2 à la première
 *                                       graduation sans convertir »)
 *
 * Spécification (914.json) :
 *   Bornes appliquées : graduation_secondaire = liste fermée { moitié, tiers,
 *     quart, dixième, centième }, plus la graduation à l'unité du niveau 1 ;
 *     nombre_de_decimales_de_l_abscisse 0..2 ;
 *     abscisse_de_l_origine_de_la_fenetre -6..-1, toujours strictement
 *     négative et jamais un nombre rond (les bornes de la fenêtre ne sont pas
 *     étiquetées) ; nombre_de_points_par_droite 2..4 ;
 *     rang_du_point_depuis_l_origine_de_la_fenetre 0..6 graduations
 *     principales (largeur de fenêtre calée dessus), rangs pris dans des
 *     intervalles DISJOINTS ; position_sur_une_graduation_principale : au
 *     moins un point par droite tombe sur une graduation principale non
 *     étiquetée ; tolerance_de_placement = la moitié du pas secondaire de la
 *     question (le snap du composant, jamais un réglage global).
 *   Exclusions : la fenêtre ne commence jamais à zéro ni à un nombre rond ;
 *     deux points n'ont jamais la même abscisse (intervalles disjoints) ;
 *     aucune abscisse à plus de deux décimales ; sur la droite en tiers,
 *     aucune abscisse décimale (la réponse est une fraction, la consigne le
 *     dit) ; aucun point hors de la fenêtre tracée (les points sont pris sur
 *     la grille, bornes exclues).
 *   Erreurs visées → mots-clés (_meta.params.distracteurs[].code,
 *   _meta.params.lignes[].code, _meta.params.erreurVisee) :
 *     « Compter les graduations depuis le bord gauche supposé nul »
 *         → bord_gauche_suppose_nul (lire_qcm_unique, lire_texte_trou_drag, lire_vf)
 *     « Lire la graduation secondaire comme une unité »
 *         → graduation_lue_comme_unite (lire_qcm_unique, lire_texte_trou_drag)
 *     « Compter un point négatif dans le sens des positifs (-2,3 au lieu de -1,7) »
 *         → negatif_compte_a_l_envers (lire_qcm_unique, lire_texte_trou_drag, lire_vf)
 *     « Placer un nombre à une décimale à la première graduation d'une droite
 *       au centième » → decimale_non_convertie (placer_centiemes_placer_points)
 *     « Confondre le rang d'un point et sa distance à l'origine de la fenêtre »
 *         → rang_confondu_avec_distance (lire_qcm_unique, lire_vf)
 *     « Rendre une valeur décimale approchée au lieu de la fraction exacte »
 *         → fraction_rendue_en_decimal (lire_tiers_texte)
 *     « Supposer que le point nommé A est toujours le plus à gauche »
 *         → nom_suppose_ordonne (lire_vf, et la consigne de chaque carte le dit)
 *   Graduation → cartes :
 *     niveau 1 (entiers, graduation à l'unité, deux graduations étiquetées)
 *       → régime `unite` de la carte lire, placer_unite ;
 *     niveau 2 (moitiés puis quarts) → régimes `moities` et `quarts`,
 *       placer_demis, placer_quarts ;
 *     niveau 3 (dixièmes, une décimale) → régime `dixiemes`, placer_dixiemes ;
 *     niveau 4 (tiers, abscisses fractionnaires) → lire_tiers_texte ;
 *     niveau 5 (centièmes, deux décimales) → régime `centiemes`, placer_centiemes.
 *   Non tranché : la fenêtre au centième ne peut pas contenir zéro tout en
 *     commençant entre -6 et -1 (limite de 100 graduations du composant) ;
 *     le placement sur une droite en tiers est impossible (le composant émet
 *     une valeur décimale, que la spec refuse) ; « série » de 2 à 4 droites
 *     n'a pas d'équivalent (un tirage = une droite).
 *
 * Arithmétique interne en MILLIÈMES entiers pour les graduations décimales, en
 * TIERS entiers pour la droite en tiers (zéro flottant des deux côtés).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, basePayload,
    filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 914;

const L_LIRE = 'Lire l\'abscisse d\'un point';
const L_PLACER = 'Placer des points sur la droite graduée';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** points_de_vigilance : les noms ne suivent pas l'ordre de gauche à droite. */
const RAPPEL_NOMS = 'Les noms des points ne suivent pas l\'ordre de gauche à droite.';
const BAREME = 'Chaque point compte pour un point.';

// millièmes → écriture française canonique (« -3,6 », « 0,06 », « -2 », « 0 »).
const frM = (m) => {
    const s = m < 0 ? '-' : '';
    const a = Math.abs(m);
    const ent = Math.floor(a / 1000);
    const frac = String(a % 1000).padStart(3, '0').replace(/0+$/, '');
    return s + (frac ? `${ent},${frac}` : String(ent));
};

const LETTRES_PTS = ['B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'M'];
/** Les lettres d'une droite : A est TOUJOURS servie, mais jamais forcément à gauche. */
const tirerLettres = (n) => shuffle(['A', ...shuffle(LETTRES_PTS).slice(0, n - 1)]);

// ═════════ Régimes de graduation (liste fermée de la spécification) ═════════
// `pas` = graduation secondaire, `principal` = graduation principale (celle qui
// est étiquetée), `largeur` = étendue de la fenêtre, le tout en millièmes.

const REGIMES = {
    unite: {
        pas: 1000, principal: 1000, largeur: 6000, niveau: 1, nbReperes: 2, consecutifs: true,
        // fenêtre entière : ses bornes ne sont PAS étiquetées, l'élève part des
        // deux seules graduations nommées (graduation_difficulte, niveau 1).
        debut: () => -randInt(3, 6) * 1000,
    },
    moities: {
        pas: 500, principal: 1000, largeur: 6000, niveau: 2, nbReperes: 3,
        debut: () => -(randInt(1, 5) * 1000 + 500),
    },
    quarts: {
        pas: 250, principal: 1000, largeur: 5000, niveau: 2, nbReperes: 3,
        debut: () => -(randInt(1, 4) * 1000 + randomChoice([250, 500, 750])),
    },
    dixiemes: {
        pas: 100, principal: 1000, largeur: 5000, niveau: 3, nbReperes: 3,
        debut: () => -(randInt(1, 4) * 1000 + randInt(1, 9) * 100),
    },
    centiemes: {
        pas: 10, principal: 100, largeur: 600, niveau: 5, nbReperes: 3,
        debut: () => -(randInt(1, 5) * 1000 + randInt(1, 9) * 100 + randInt(1, 9) * 10),
    },
};

/** Combien de graduations SECONDAIRES négatives restent libres (hors bornes, hors repères) ? */
const libresNegatifs = (start, end, pas, reperes) => {
    let c = 0;
    for (let m = start + pas; m <= end - pas; m += pas) if (m < 0 && !reperes.includes(m)) c++;
    return c;
};

/**
 * Les étiquettes d'abscisse tiennent-elles côte à côte ?
 *
 * Le composant écrit une valeur sous la PREMIÈRE graduation, sous la DERNIÈRE, et sous
 * chaque repère. Rien ne vérifie qu'elles ne se recouvrent pas : « −4,8 » et « −4,79 »
 * se retrouvaient à 9 pixels l'un de l'autre alors qu'il en faut 27 (signalé par le
 * prof le 2026-09-25). La cause : les bornes de la fenêtre ne tombent pas sur la grille
 * principale, un repère peut donc se coller au bord.
 *
 * ⚠️ Ce calcul recopie la géométrie de NumberLineInteractiveEleve : largeur 600, marges
 * 25 et 35, fonte 10 dans la vue normale, la plus serrée. Si elles changent là-bas, ce
 * garde-fou devient faux — d'où le test jetable qui refait le même calcul sur le vrai
 * composant.
 */
const ETIQ_UTILE = 600 - 25 - 35;
const ETIQ_PAR_CAR = 0.55 * 10;
const largeurEtiquette = (millimes) =>
    String(Math.round(millimes) / 1000).replace('.', ',').length * ETIQ_PAR_CAR;

const etiquettesLisibles = (start, end, reperes) => {
    const bornes = [start, ...reperes, end].sort((a, b) => a - b);
    const px = (m) => ((m - start) / (end - start)) * ETIQ_UTILE;
    for (let i = 1; i < bornes.length; i++) {
        const besoin = (largeurEtiquette(bornes[i - 1]) + largeurEtiquette(bornes[i])) / 2 + 2;
        if (px(bornes[i]) - px(bornes[i - 1]) < besoin) return false;
    }
    return true;
};

/** Les graduations principales de la fenêtre (en millièmes). */
const principalesDe = (start, end, principal) => {
    const out = [];
    for (let m = Math.ceil(start / principal) * principal; m <= end; m += principal) out.push(m);
    return out;
};

/**
 * Construit une fenêtre : bornes, pas, graduations principales étiquetées.
 * Au moins une graduation principale reste NON étiquetée, pour que le point
 * « sur une graduation principale » ne soit pas donné par son étiquette.
 */
const tirerFenetre = (nom) => {
    const reg = REGIMES[nom];
    const start = reg.debut();
    const end = start + reg.largeur;
    const principales = principalesDe(start, end, reg.principal).filter(m => m > start && m < end);
    const tirerReperes = () => (reg.consecutifs
        ? [principales[randInt(0, principales.length - 2)]].flatMap(m => [m, m + reg.principal])
        : shuffle(principales).slice(0, reg.nbReperes).sort((a, b) => a - b));
    // Les repères ne doivent jamais confisquer TOUTES les graduations négatives :
    // au moins une abscisse négative doit rester plaçable.
    // Deux conditions : au moins une graduation négative reste plaçable, ET les
    // étiquettes d'abscisse ne se recouvrent pas.
    const convient = (rs) => libresNegatifs(start, end, reg.pas, rs) >= 1
        && etiquettesLisibles(start, end, rs);
    let reperes = tirerReperes();
    for (let essai = 0; essai < 40 && !convient(reperes); essai++) {
        reperes = tirerReperes();
    }
    if (!convient(reperes)) {
        // Repli déterministe : on retire les repères collés à un bord, puis, s'il le
        // faut encore, ceux qui privent le négatif de toute graduation libre.
        reperes = reperes.filter(m => etiquettesLisibles(start, end, [m]));
        if (libresNegatifs(start, end, reg.pas, reperes) < 1) reperes = reperes.filter(m => m >= 0);
    }
    return { nom, start, end, pas: reg.pas, principal: reg.principal, niveau: reg.niveau, reperes, principales };
};

/**
 * n abscisses DISTINCTES, prises dans des intervalles DISJOINTS de la fenêtre
 * (jamais sur une borne, jamais sur une graduation étiquetée), dont au moins
 * une sur une graduation principale (position_sur_une_graduation_principale).
 */
const tirerAbscisses = (f, nVoulu, forcerNegatif = false) => {
    const libres = [];
    for (let m = f.start + f.pas; m <= f.end - f.pas; m += f.pas) {
        if (!f.reperes.includes(m)) libres.push(m);
    }
    // Une zone par point, jamais plus de points que de positions libres :
    // deux points ne portent alors jamais la même abscisse.
    const n = Math.max(2, Math.min(nVoulu, libres.length));
    const taille = Math.floor(libres.length / n);
    const zones = [];
    for (let i = 0; i < n; i++) {
        zones.push(libres.slice(i * taille, i === n - 1 ? libres.length : (i + 1) * taille));
    }
    const vals = zones.map(z => randomChoice(z));
    const estPrincipal = (m) => m % f.principal === 0;
    const satisfait = () => vals.some(estPrincipal) && (!forcerNegatif || vals.some(m => m < 0));
    // Deux contraintes de la spec, réglées ENSEMBLE (les régler l'une après
    // l'autre défaisait la première) : position_sur_une_graduation_principale,
    // et au moins une abscisse négative, cœur de l'attendu.
    if (!satisfait()) {
        let regle = false;
        for (let i = 0; i < n && !regle; i++) {
            const principaux = zones[i].filter(estPrincipal);
            if (!principaux.length) continue;
            if (!forcerNegatif) { vals[i] = randomChoice(principaux); regle = true; break; }
            for (let j = 0; j < n && !regle; j++) {
                if (j === i) continue;
                const negatifs = zones[j].filter(m => m < 0);
                if (negatifs.length) {
                    vals[i] = randomChoice(principaux);
                    vals[j] = randomChoice(negatifs);
                    regle = true;
                }
            }
        }
        // Repli déterministe : une même zone offre un point à la fois négatif
        // et principal.
        for (let i = 0; i < n && !regle; i++) {
            const deux = zones[i].filter(m => m < 0 && estPrincipal(m));
            if (deux.length) { vals[i] = deux[0]; regle = true; }
        }
        // Dernier repli : la négativité prime, c'est le cœur de l'attendu.
        for (let i = 0; i < n && !regle && forcerNegatif && !vals.some(m => m < 0); i++) {
            const negatifs = zones[i].filter(m => m < 0);
            if (negatifs.length) { vals[i] = negatifs[0]; regle = true; }
        }
        // … et la graduation principale sort alors de sa zone, les abscisses
        // restant toutes distinctes.
        if (!vals.some(estPrincipal)) {
            const principauxLibres = libres.filter(m => estPrincipal(m) && !vals.includes(m));
            const cible = vals.findIndex(m => m >= 0);
            const i = cible >= 0 ? cible : (vals.filter(m => m < 0).length > 1 ? 0 : -1);
            if (principauxLibres.length && i >= 0) vals[i] = randomChoice(principauxLibres);
        }
    }
    return vals;
};

const elementDroite = (f, valsM, lettres) => ({
    version: 1,
    elements: [{
        type: 'numberline',
        config: {
            start: f.start / 1000, end: f.end / 1000, step: f.pas / 1000,
            reperes: f.reperes.map(m => m / 1000),
            // Les bornes de la fenêtre ne sont JAMAIS étiquetées : le bord
            // gauche ne doit pas pouvoir être lu (exclusion « la fenêtre ne
            // commence jamais à zéro ni à un nombre rond »).
            showStart: false, showEnd: false, lineType: 'full',
            points: valsM.map((m, i) => ({ value: m / 1000, label: lettres[i], showValue: false })),
        },
    }],
});

/** La graduation principale immédiatement inférieure ou égale à m. */
const basePrincipale = (f, m) => Math.floor(m / f.principal) * f.principal;
/** Le rang du point depuis cette graduation principale, en pas secondaires. */
const rangDepuisBase = (f, m) => (m - basePrincipale(f, m)) / f.pas;

/**
 * Les erreurs de lecture, chacune calculée sur la fenêtre et le point.
 * Le facteur du « pas lu comme une unité » dépend du régime : un dixième lu
 * comme une unité, un centième lu comme un dixième, un demi ou un quart
 * compté en dixièmes.
 */
const erreursDeLecture = (f, m) => {
    const base = basePrincipale(f, m);
    const k = rangDepuisBase(f, m);
    const facteur = f.pas === 100 ? 1000 : 100; // dixième → unité ; sinon → dixième
    return [
        { valeur: m - f.start, code: 'bord_gauche_suppose_nul' },
        { valeur: base + k * facteur, code: 'graduation_lue_comme_unite' },
        { valeur: 2 * base - m, code: 'negatif_compte_a_l_envers' },
        { valeur: (m < 0 ? -1 : 1) * k * 1000, code: 'rang_confondu_avec_distance' },
        { valeur: m + f.pas, code: 'graduation_voisine' },
        { valeur: m - f.pas, code: 'graduation_voisine' },
    ];
};

/** Retient les n premières erreurs distinctes, différentes de la bonne valeur. */
const distracteursDe = (f, m, n) => {
    const vus = new Set([frM(m)]);
    const out = [];
    erreursDeLecture(f, m).forEach((e) => {
        const t = frM(e.valeur);
        if (out.length < n && !vus.has(t)) { vus.add(t); out.push({ valeur: t, code: e.code }); }
    });
    // Repli déterministe : des graduations voisines de plus en plus éloignées.
    for (let j = 2; out.length < n && j <= 6; j++) {
        [m + j * f.pas, m - j * f.pas].forEach((v) => {
            const t = frM(v);
            if (out.length < n && !vus.has(t)) { vus.add(t); out.push({ valeur: t, code: 'graduation_voisine' }); }
        });
    }
    return out;
};

const REGIMES_LIRE = ['unite', 'moities', 'quarts', 'dixiemes', 'centiemes'];

// ═════════ Carte « lire » — Lire l'abscisse d'un point ═════════

const genLireDrag = () => {
    const f = tirerFenetre(randomChoice(REGIMES_LIRE));
    const n = randInt(2, 4);
    const vals = tirerAbscisses(f, n);
    const lettres = tirerLettres(vals.length);
    // Une question par ligne (convention 2026-09-02 : jamais « ; » entre deux
    // trous indépendants, l'impression coupait au milieu).
    const phrase = vals.map((m, i) => `Le point ${lettres[i]} a pour abscisse {{${frM(m)}}}.`).join('\n');
    const candidats = vals.flatMap(m => distracteursDe(f, m, 2));
    const distracteurs = filtrerDistracteurs(phrase, candidats.map(d => d.valeur)).slice(0, 4);
    const payload = makeTexteTrou(phrase,
        `<p><b>Donne l'abscisse de chaque point.</b> ${RAPPEL_NOMS} ${BAREME}</p>`,
        {
            ...opts(L_LIRE), mode: 'drag', distracteurs,
            elements_additionnels: elementDroite(f, vals, lettres),
        });
    payload._meta.params = {
        regime: f.nom, niveau: f.niveau, start: f.start, vals,
        distracteurs: distracteurs.map(t => ({
            valeur: t, code: (candidats.find(c => c.valeur === t) || {}).code || 'graduation_voisine',
        })),
        erreurVisee: 'nom_suppose_ordonne',
    };
    return payload;
};

const genLireQcm = () => {
    const f = tirerFenetre(randomChoice(REGIMES_LIRE));
    const n = randInt(2, 4);
    const vals = tirerAbscisses(f, n);
    const lettres = tirerLettres(vals.length);
    const cible = randInt(0, vals.length - 1);
    const m = vals[cible];
    const distracteurs = distracteursDe(f, m, 3);
    const payload = makeQcm([frM(m), ...distracteurs.map(d => d.valeur)], frM(m),
        `<p><b>Quelle est l'abscisse du point ${lettres[cible]} ?</b> ${RAPPEL_NOMS}</p>`,
        { ...opts(L_LIRE), elements_additionnels: elementDroite(f, vals, lettres) });
    payload._meta.params = { regime: f.nom, niveau: f.niveau, start: f.start, m, distracteurs };
    return payload;
};

const genLireSaisie = () => {
    const f = tirerFenetre(randomChoice(REGIMES_LIRE));
    const n = randInt(2, 4);
    const vals = tirerAbscisses(f, n);
    const lettres = tirerLettres(vals.length);
    const cible = randInt(0, vals.length - 1);
    const m = vals[cible];
    const payload = makeSaisie(frM(m),
        `<p><b>Quelle est l'abscisse du point ${lettres[cible]} ?</b> `
        + `Écris-la en écriture décimale ; un nombre positif peut s'écrire avec ou sans le signe +. ${RAPPEL_NOMS}</p>`,
        { ...opts(L_LIRE), elements_additionnels: elementDroite(f, vals, lettres) });
    payload._meta.params = {
        regime: f.nom, niveau: f.niveau, start: f.start, m,
        ...(m < 0 ? { erreurVisee: 'negatif_compte_a_l_envers' } : { erreurVisee: 'bord_gauche_suppose_nul' }),
    };
    return payload;
};

/**
 * Gabarits de V/F à prédicat variable : chacun produit une ligne VRAIE ou
 * FAUSSE selon `v`. ctx : { f, vals, lettres, i, j, ecart }.
 */
const GABARITS_LIRE = [
    {
        id: 'abscisse', code: 'bord_gauche_suppose_nul',
        ligne: (v, c) => `L'abscisse du point ${c.lettres[c.i]} est `
            + `${frM(v ? c.vals[c.i] : c.vals[c.i] - c.f.start)}.`,
    },
    {
        id: 'signe', code: 'negatif_compte_a_l_envers',
        ligne: (v, c) => {
            const k = c.iSigne;
            const negatif = v ? c.vals[k] < 0 : c.vals[k] > 0;
            return `Le point ${c.lettres[k]} a une abscisse ${negatif ? 'négative' : 'positive'}.`;
        },
    },
    {
        id: 'gauche', code: 'nom_suppose_ordonne',
        ligne: (v, c) => {
            const minimum = Math.min(...c.vals);
            const lettre = v
                ? c.lettres[c.vals.indexOf(minimum)]
                : c.lettres[c.vals.indexOf(Math.max(...c.vals))];
            return `Le point ${lettre} est le point le plus à gauche de la droite graduée.`;
        },
    },
    {
        id: 'rang', code: 'rang_confondu_avec_distance',
        ligne: (v, c) => `Le point ${c.lettres[c.j]} est à `
            + `${(c.vals[c.j] - c.f.start) / c.f.pas + (v ? 0 : c.ecart)} graduations du bord gauche de la droite graduée.`,
    },
    {
        id: 'ordre', code: 'negatif_compte_a_l_envers',
        ligne: (v, c) => {
            const petit = c.vals[c.i] < c.vals[c.j];
            const [a, b] = (v === petit) ? [c.i, c.j] : [c.j, c.i];
            return `L'abscisse du point ${c.lettres[a]} est plus petite que celle du point ${c.lettres[b]}.`;
        },
    },
];

/** Tire nb vérités avec au moins une vraie et une fausse. */
const tirerVerites = (nb) => {
    const v = [];
    for (let i = 0; i < nb; i++) v.push(randomChoice([true, false]));
    if (v.every(x => x)) v[randInt(0, nb - 1)] = false;
    else if (v.every(x => !x)) v[randInt(0, nb - 1)] = true;
    return v;
};

const genLireVf = () => {
    const f = tirerFenetre(randomChoice(REGIMES_LIRE));
    const vals = tirerAbscisses(f, 4);
    const lettres = tirerLettres(vals.length);
    const i = randInt(0, vals.length - 1);
    let j = randInt(0, vals.length - 1);
    if (j === i) j = (i + 1) % vals.length; // repli déterministe
    // 0 est à la fois positif et négatif : le gabarit de signe l'évite.
    const nonNuls = vals.map((m, k) => k).filter(k => vals[k] !== 0);
    const iSigne = nonNuls.length ? randomChoice(nonNuls) : i;
    const ctx = { f, vals, lettres, i, j, iSigne, ecart: randomChoice([-2, -1, 1, 2]) };
    const gabarits = shuffle(GABARITS_LIRE).slice(0, 4);
    const verites = tirerVerites(4);
    const lignes = gabarits.map((g, k) => ({
        template: g.id, code: g.code, vrai: verites[k], texte: g.ligne(verites[k], ctx),
    }));
    const payload = makeVraiFaux(
        lignes.map(l => l.texte),
        lignes.map(l => (l.vrai ? 0 : 1)),
        `<p><b>Observe la droite graduée. Pour chaque affirmation, coche Vrai ou Faux.</b> ${RAPPEL_NOMS}</p>`,
        { ...opts(L_LIRE), elements_additionnels: elementDroite(f, vals, lettres) });
    payload._meta.params = { regime: f.nom, niveau: f.niveau, start: f.start, vals, lignes };
    return payload;
};

// ═════════ Droite graduée en TIERS (niveau 4) ═════════
// Arithmétique en TIERS entiers. Aucune abscisse décimale : la réponse
// attendue est une fraction, et la consigne le dit (exclusion de la spec).

const tirerFenetreTiers = () => {
    // Origine strictement entre -6 et -1, jamais sur un entier.
    const start = -(randInt(1, 4) * 3 + randomChoice([1, 2]));
    const end = start + 15; // 5 unités, 15 graduations
    const principales = [];
    for (let t = Math.ceil(start / 3) * 3; t <= end; t += 3) principales.push(t);
    const reperes = shuffle(principales).slice(0, 3).sort((a, b) => a - b);
    return { start, end, principales, reperes };
};

const elementDroiteTiers = (f, valsT, lettres) => ({
    version: 1,
    elements: [{
        type: 'numberline',
        config: {
            start: f.start / 3, end: f.end / 3, step: 1 / 3,
            reperes: f.reperes.map(t => t / 3),
            showStart: false, showEnd: false, lineType: 'full',
            points: valsT.map((t, i) => ({ value: t / 3, label: lettres[i], showValue: false })),
        },
    }],
});

const genLireTiers = () => {
    const f = tirerFenetreTiers();
    const n = randInt(2, 4);
    const libres = [];
    for (let t = f.start + 1; t <= f.end - 1; t++) if (!f.reperes.includes(t)) libres.push(t);
    const nb = Math.max(2, Math.min(n, libres.length));
    const taille = Math.floor(libres.length / nb);
    const zones = [];
    for (let i = 0; i < nb; i++) zones.push(libres.slice(i * taille, i === nb - 1 ? libres.length : (i + 1) * taille));
    const vals = zones.map(z => randomChoice(z));
    // position_sur_une_graduation_principale : un point sur un entier.
    if (!vals.some(t => t % 3 === 0)) {
        for (let i = 0; i < zones.length; i++) {
            const entiers = zones[i].filter(t => t % 3 === 0);
            if (entiers.length) { vals[i] = randomChoice(entiers); break; }
        }
    }
    // Le point interrogé n'est PAS sur une graduation principale : la réponse
    // est une vraie fraction.
    const candidats = vals.map((t, i) => i).filter(i => vals[i] % 3 !== 0);
    const cible = candidats.length ? randomChoice(candidats) : 0;
    const t = vals[cible];
    const lettres = tirerLettres(vals.length);
    // Écritures admises : la fraction en tiers et ses deux amplifications ;
    // l'entier quand le point tombe sur une graduation principale (repli).
    const reponses = [`${t}/3`, `${t * 2}/6`, `${t * 3}/9`, ...(t % 3 === 0 ? [String(t / 3)] : [])];
    const payload = makeSaisie(reponses[0],
        `<p><b>Quelle est l'abscisse du point ${lettres[cible]} ?</b> `
        + 'Cette droite est graduée en tiers : écris ta réponse sous la forme d\'une fraction, '
        + `par exemple -5/3. ${RAPPEL_NOMS}</p>`,
        {
            ...opts(L_LIRE), comparaison: 'expression', reponses_acceptees: reponses,
            elements_additionnels: elementDroiteTiers(f, vals, lettres),
        });
    payload._meta.params = {
        regime: 'tiers', niveau: 4, start: f.start, t, vals,
        erreurVisee: 'fraction_rendue_en_decimal',
    };
    return payload;
};

// ═════════ Carte « placer » — Placer des points ═════════
// La tolérance est celle du snap du composant : la moitié du pas de LA
// question (tolerance_de_placement), jamais un réglage global.

const genPlacer = (nom) => () => {
    const f = tirerFenetre(nom);
    const n = randInt(2, 4);
    const vals = shuffle(tirerAbscisses(f, n, true));
    const lettres = tirerLettres(vals.length);
    const bonne = {};
    vals.forEach((m, i) => { bonne[lettres[i]] = frM(m); });
    const consigne = vals.map((m, i) => `${lettres[i]} d'abscisse ${frM(m)}`).join(', ');
    const payload = basePayload('association',
        `<p><b>Place les points ${consigne} sur la droite graduée.</b> ${BAREME}</p>`,
        {
            subType: 'placer_points',
            numberline: {
                start: f.start / 1000, end: f.end / 1000, step: f.pas / 1000,
                reperes: f.reperes.map(m => m / 1000),
            },
            pointsToPlace: lettres,
            bonne_reponse: JSON.stringify(bonne),
        },
        opts(L_PLACER));
    payload._meta.params = {
        regime: nom, niveau: f.niveau, start: f.start, vals,
        // Sur la droite au centième, au moins une abscisse s'écrit avec une
        // seule décimale : l'élève qui ne convertit pas la place dix fois trop
        // près de la graduation principale.
        ...(nom === 'centiemes' ? { erreurVisee: 'decimale_non_convertie' } : {}),
    };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    lire_texte_trou_drag: genLireDrag,
    lire_qcm_unique: genLireQcm,
    lire_texte: genLireSaisie,
    lire_vf: genLireVf,
    lire_tiers_texte: genLireTiers,
    placer_unite_placer_points: genPlacer('unite'),
    placer_demis_placer_points: genPlacer('moities'),
    placer_quarts_placer_points: genPlacer('quarts'),
    placer_dixiemes_placer_points: genPlacer('dixiemes'),
    placer_centiemes_placer_points: genPlacer('centiemes'),
};

const FORMAT_LABELS = {
    texte_trou_drag: 'Drag', qcm_unique: 'QCM', texte: 'Saisie', vf: 'Vrai/Faux',
    tiers_texte: 'Saisie (tiers)',
    unite_placer_points: 'Placer (1)', demis_placer_points: 'Placer (0,5)',
    quarts_placer_points: 'Placer (0,25)', dixiemes_placer_points: 'Placer (0,1)',
    centiemes_placer_points: 'Placer (0,01)',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'lire', label: L_LIRE, formatsList: buildFormats('lire') },
    { id: 'placer', label: L_PLACER, formatsList: buildFormats('placer') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
